import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi, googleApi, type AnalyticsConfigInput } from '../lib/api';

export function useAnalyticsConfig(siteId: string) {
  return useQuery({
    queryKey: ['analytics', siteId],
    queryFn: async () => {
      const response = await analyticsApi.get(siteId);
      return response.data;
    },
    enabled: !!siteId,
  });
}

export function useGoogleStatus(siteId: string) {
  return useQuery({
    queryKey: ['googleStatus', siteId],
    queryFn: async () => {
      const response = await googleApi.getStatus(siteId);
      return response.data;
    },
    enabled: !!siteId,
  });
}

export function useGA4Accounts(siteId: string, enabled = true) {
  return useQuery({
    queryKey: ['ga4Accounts', siteId],
    queryFn: async () => {
      const response = await analyticsApi.getAccounts(siteId);
      return response.data;
    },
    enabled: !!siteId && enabled,
  });
}

export function useGA4Properties(siteId: string, accountId: string | null) {
  return useQuery({
    queryKey: ['ga4Properties', siteId, accountId],
    queryFn: async () => {
      if (!accountId) return [];
      const response = await analyticsApi.getProperties(siteId, accountId);
      return response.data;
    },
    enabled: !!siteId && !!accountId,
  });
}

export function useGA4DataStreams(siteId: string, propertyId: string | null) {
  return useQuery({
    queryKey: ['ga4DataStreams', siteId, propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const response = await analyticsApi.getDataStreams(siteId, propertyId);
      return response.data;
    },
    enabled: !!siteId && !!propertyId,
  });
}

export function useConfigureAnalytics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      siteId,
      config,
    }: {
      siteId: string;
      config: AnalyticsConfigInput;
    }) => {
      const response = await analyticsApi.configure(siteId, config);
      return response.data;
    },
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: ['analytics', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export function useApplyCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (siteId: string) => {
      const response = await analyticsApi.applyCode(siteId);
      return response.data;
    },
    onSuccess: (_, siteId) => {
      queryClient.invalidateQueries({ queryKey: ['analytics', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });
}

export function useRemoveCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (siteId: string) => {
      await analyticsApi.removeCode(siteId);
      return siteId;
    },
    onSuccess: (siteId) => {
      queryClient.invalidateQueries({ queryKey: ['analytics', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });
}

export function useCheckCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (siteId: string) => {
      const response = await analyticsApi.checkCode(siteId);
      return response.data;
    },
    onSuccess: (_, siteId) => {
      queryClient.invalidateQueries({ queryKey: ['analytics', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });
}

export function useDisconnectGoogle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (siteId: string) => {
      await googleApi.disconnect(siteId);
      return siteId;
    },
    onSuccess: (siteId) => {
      queryClient.invalidateQueries({ queryKey: ['googleStatus', siteId] });
      queryClient.invalidateQueries({ queryKey: ['analytics', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });
}

export function useCreateProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      siteId,
      accountId,
      propertyName,
      websiteUrl,
    }: {
      siteId: string;
      accountId: string;
      propertyName: string;
      websiteUrl: string;
    }) => {
      const response = await analyticsApi.createProperty(
        siteId,
        accountId,
        propertyName,
        websiteUrl
      );
      return response;
    },
    onSuccess: (_, { siteId }) => {
      // Invalidate queries to refresh property list and analytics config
      queryClient.invalidateQueries({ queryKey: ['ga4Properties', siteId] });
      queryClient.invalidateQueries({ queryKey: ['analytics', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}
